import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Pagination, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TableChartIcon from '@mui/icons-material/TableChart';
import { usePermissions } from '../../../utils/usePermissions';
import { ConfirmDialog, EditEmptyState } from '../reports/edit/EditCommon';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';
import { PRIMARY_BUTTON_SX } from '../mathReports/mathTableStyles';
import ColumnFormDialog from './revenue/ColumnFormDialog';
import OperationPanel from './revenue/OperationPanel';
import RecalcDialog from './revenue/RecalcDialog';
import RevenueColumnsTable from './revenue/RevenueColumnsTable';
import { filterColumns, pageCount, pageSlice } from './revenue/revenueCalc';
import type { RevenueColumn } from './revenue/revenueApi';
import { useRevenue } from './revenue/useRevenue';

const BORDER = '1px solid #e2e8f0';

/** Форма, диалог пересчета и диалог удаления остаются смонтированными на время закрытия, чтобы текст не пропадал посреди анимации */
interface FormState {
  key: number;
  target: RevenueColumn | null;
  open: boolean;
}

interface RecalcState {
  key: number;
  open: boolean;
}

interface DeleteState {
  column: RevenueColumn;
  open: boolean;
}

const PharmacyTable: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canCreate = hasPerm('tables.revenue.create');
  const canEdit = hasPerm('tables.revenue.edit');
  const canDelete = hasPerm('tables.revenue.delete');
  const canEditFormula = hasPerm('tables.revenue.formula.edit');
  const canSync = hasPerm('tables.revenue.sync.run');
  const canSeeLock = hasPerm('tables.revenue.lock.view');
  const canRelease = hasPerm('tables.revenue.lock.release');
  const canPreviewRecalc = hasPerm('tables.revenue.recalc.preview');
  const canRunRecalc = hasPerm('tables.revenue.recalc.run');
  const canRecalc = canPreviewRecalc || canRunRecalc;
  // Состояние операций нужно тем, кто их видит или запускает: остальным опрос сервера ни к чему
  const watchOperations = canSeeLock || canSync || canRecalc;

  const {
    data,
    loading,
    error,
    retry,
    pending,
    operation,
    busy,
    canRepeat,
    repeatJob,
    dismissJob,
    saveColumn,
    removeColumn,
    startSync,
    startRecalcFrom,
    releaseDataLock
  } = useRevenue(watchOperations);

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<FormState | null>(null);
  const [recalc, setRecalc] = useState<RecalcState | null>(null);
  const [removal, setRemoval] = useState<DeleteState | null>(null);

  useEffect(() => {
    document.title = 'Таблица выручки | Zerde';
  }, []);

  const columns = data ? data.columns : null;
  const pharmacyTables = data ? data.pharmacyTables : null;

  const filtered = useMemo(() => (columns ? filterColumns(columns, query) : []), [columns, query]);
  const pages = pageCount(filtered.length);
  // После удаления или поиска страница может оказаться за последней
  const currentPage = Math.min(page, pages);
  const visible = useMemo(() => pageSlice(filtered, currentPage), [filtered, currentPage]);

  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setPage(1);
  }, []);
  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => setPage(value), []);

  // Каждое открытие формы получает новый key: поля заполняются заново, а не хранят прежний ввод
  const openForm = useCallback(
    (target: RevenueColumn | null) => setForm((prev) => ({ key: (prev ? prev.key : 0) + 1, target, open: true })),
    []
  );
  const handleCreate = useCallback(() => openForm(null), [openForm]);
  const closeForm = useCallback(() => setForm((prev) => prev && { ...prev, open: false }), []);

  const openRecalc = useCallback(() => setRecalc((prev) => ({ key: (prev ? prev.key : 0) + 1, open: true })), []);
  const closeRecalc = useCallback(() => setRecalc((prev) => prev && { ...prev, open: false }), []);

  const handleAskDelete = useCallback((column: RevenueColumn) => setRemoval({ column, open: true }), []);
  const closeRemoval = useCallback(() => setRemoval((prev) => prev && { ...prev, open: false }), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!removal) return;
    if (await removeColumn(removal.column)) closeRemoval();
  }, [removal, removeColumn, closeRemoval]);

  const renderContent = () => {
    if (error && !columns) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить колонки таблицы"
          text={error}
          action={<Button variant="outlined" onClick={retry} sx={ACTION_BUTTON_SX}>Повторить</Button>}
        />
      );
    }
    if (!columns) {
      return (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (columns.length === 0) {
      return (
        <EditEmptyState
          icon={<TableChartIcon />}
          title="Колонок пока нет"
          text={canCreate ? 'Добавьте первую колонку: она появится в отчетах аптек.' : undefined}
          action={
            canCreate ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={PRIMARY_BUTTON_SX}>
                Добавить колонку
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EditEmptyState
          icon={<SearchOffIcon />}
          title="Ничего не найдено"
          text="Колонок с таким именем или группой нет. Проверьте запрос."
        />
      );
    }
    return (
      <>
        <Paper
          elevation={0}
          sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
          style={{ opacity: loading ? 0.55 : 1 }}
        >
          <RevenueColumnsTable
            columns={visible}
            pharmacyTables={pharmacyTables}
            canEdit={canEdit}
            canDelete={canDelete}
            canSync={canSync}
            busy={busy}
            onEdit={openForm}
            onDelete={handleAskDelete}
            onSync={startSync}
          />
        </Paper>
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={pages} page={currentPage} onChange={handlePageChange} shape="rounded" color="primary" />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <OperationPanel
        state={operation}
        pending={pending}
        canRelease={canRelease}
        canRepeat={canRepeat && (canSync || canRunRecalc)}
        onRepeat={repeatJob}
        onDismiss={dismissJob}
        onRelease={releaseDataLock}
      />

      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, maxWidth: 450 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Поиск по имени, названию или группе"
            value={query}
            onChange={handleQueryChange}
            disabled={!columns}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                </InputAdornment>
              )
            }}
          />
          {columns && (
            <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
              Всего: <b>{filtered.length}</b>
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {canRecalc && columns && columns.length > 0 && (
            <Button variant="outlined" onClick={openRecalc} disabled={busy} sx={ACTION_BUTTON_SX}>
              Пересчитать историю
            </Button>
          )}
          {canCreate && columns && columns.length > 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={PRIMARY_BUTTON_SX}>
              Добавить колонку
            </Button>
          )}
        </Box>
      </Box>

      {renderContent()}

      {form && columns && (
        <ColumnFormDialog
          key={form.key}
          open={form.open}
          target={form.target}
          columns={columns}
          saving={pending === 'save'}
          canEditFormula={canEditFormula}
          canSync={canSync}
          canRecalc={canRecalc}
          busy={busy}
          onSave={saveColumn}
          onClose={closeForm}
        />
      )}

      {recalc && (
        <RecalcDialog
          key={recalc.key}
          open={recalc.open}
          canPreview={canPreviewRecalc}
          canRun={canRunRecalc}
          starting={pending === 'recalc'}
          busy={busy}
          onStart={startRecalcFrom}
          onClose={closeRecalc}
        />
      )}

      <ConfirmDialog
        open={!!removal && removal.open}
        title="Удалить колонку"
        confirmText="Удалить"
        confirmColor="error"
        loading={pending === 'delete'}
        maxWidth="xs"
        onConfirm={handleConfirmDelete}
        onClose={closeRemoval}
      >
        {removal && (
          <>
            <Typography sx={{ color: '#334155', fontSize: '0.92rem', mb: 1 }}>
              Удалить колонку <b>«{removal.column.displayName.trim()}»</b> (<code>{removal.column.cName.trim()}</code>)?
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>
              Колонка пропадет из форм отчетов аптек. Накопленные в ней значения останутся в таблицах аптек, но в отчетах
              показываться не будут.
            </Typography>
          </>
        )}
      </ConfirmDialog>
    </Box>
  );
};

export default PharmacyTable;
